import { useState } from 'react';
import { useApp, useCan } from '../../app/AppContext.js';
import { useAsync } from '../lib/useAsync.js';
import { formatBRL, formatDate, statusTone } from '../lib/format.js';
import {
  Badge,
  Button,
  Card,
  Dash,
  EmptyState,
  ErrorBlock,
  KV,
  KVRow,
  Loading,
  PageHeader,
} from '../components/primitives.js';
import { DataTable, type Column } from '../components/DataTable.js';
import { Modal } from '../components/Modal.js';
import { Field, TextInput } from '../components/Field.js';
import { useToast } from '../components/Toast.js';
import { IconCharge, IconPlus, IconRefresh } from '../components/icons.js';
import type { Charge, ChargeStatus } from '../../data/port.js';

const FILTROS: Array<{ id: string; label: string; status?: ChargeStatus }> = [
  { id: 'todos', label: 'Todos' },
  { id: 'pendente', label: 'Pendentes', status: 'PENDENTE' },
  { id: 'pago', label: 'Pagos', status: 'PAGO' },
  { id: 'liquidado', label: 'Liquidados', status: 'LIQUIDADO' },
  { id: 'expirado', label: 'Expirados', status: 'EXPIRADO' },
  { id: 'falhou', label: 'Falhas', status: 'FALHOU' },
];

const LIMITE = 100;

export function Cobrancas() {
  const { data } = useApp();
  const podeEmitir = useCan('console-operador');
  const [filtro, setFiltro] = useState('todos');
  const [detalhe, setDetalhe] = useState<Charge | null>(null);
  const [novaAberta, setNovaAberta] = useState(false);

  const alvo = FILTROS.find((f) => f.id === filtro)?.status;
  const estado = useAsync<Charge[]>(
    () => data.listCharges({ ...(alvo ? { status: alvo } : {}), limit: LIMITE }),
    [data, filtro],
  );

  const colunas: Column<Charge>[] = [
    { key: 'titulo', header: 'Título', cell: (c) => <span className="num td--strong">{c.invoiceId}</span> },
    { key: 'cliente', header: 'Cliente', cell: (c) => c.customerName ?? <Dash /> },
    { key: 'valor', header: 'Valor', num: true, cell: (c) => formatBRL(c.amountCents) },
    { key: 'vence', header: 'Vence', cell: (c) => formatDate(c.dueDate) ?? <Dash /> },
    {
      key: 'status',
      header: 'Status',
      cell: (c) => (
        <Badge tone={statusTone(c.status)} dot>
          {c.status}
        </Badge>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Cobranças"
        desc="Cobranças PIX emitidas e o estado de cada uma."
        actions={
          podeEmitir && (
            <Button variant="primary" onClick={() => setNovaAberta(true)}>
              <IconPlus size={16} /> Nova cobrança
            </Button>
          )
        }
      />

      <Card>
        <div className="card__head">
          <div className="segmented">
            {FILTROS.map((f) => (
              <button
                key={f.id}
                className={`segmented__btn${filtro === f.id ? ' segmented__btn--active' : ''}`}
                onClick={() => setFiltro(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="card__head-actions">
            {estado.data && estado.data.length > 0 && (
              <span className="result-count">{estado.data.length} cobrança(s)</span>
            )}
            <Button variant="ghost" size="sm" onClick={estado.reload} title="Recarregar">
              <IconRefresh size={15} />
            </Button>
          </div>
        </div>

        <div className="card__body--flush">
          {estado.loading && !estado.data && <Loading />}
          {estado.error && <ErrorBlock message={estado.error} />}
          {estado.data && estado.data.length === 0 && (
            <EmptyState
              icon={<IconCharge size={40} />}
              title="Nenhuma cobrança para esse filtro"
              desc="Quando o sistema emitir cobranças PIX, elas aparecem aqui. A busca por título fica em Clientes."
            />
          )}
          {estado.data && estado.data.length > 0 && (
            <DataTable
              columns={colunas}
              rows={estado.data}
              keyOf={(c) => c.invoiceId}
              onRowClick={setDetalhe}
              rowTone={(c) => statusTone(c.status)}
            />
          )}
        </div>
      </Card>

      {detalhe && <DetalheCobranca cobranca={detalhe} onClose={() => setDetalhe(null)} />}
      {novaAberta && (
        <NovaCobranca
          onClose={() => setNovaAberta(false)}
          onCriada={() => {
            setNovaAberta(false);
            estado.reload();
          }}
        />
      )}
    </>
  );
}

function DetalheCobranca({ cobranca, onClose }: { cobranca: Charge; onClose: () => void }) {
  return (
    <Modal title={`Cobrança ${cobranca.invoiceId}`} sub="Detalhes do título" onClose={onClose}>
      <KV>
        <KVRow k="Cliente">{cobranca.customerName ?? <Dash />}</KVRow>
        <KVRow k="Contrato">{cobranca.contract ?? <Dash />}</KVRow>
        <KVRow k="CPF/CNPJ">{cobranca.taxId ?? <Dash />}</KVRow>
        <KVRow k="Valor"><span className="num">{formatBRL(cobranca.amountCents)}</span></KVRow>
        <KVRow k="Vencimento">{formatDate(cobranca.dueDate) ?? <Dash />}</KVRow>
        <KVRow k="Status"><Badge tone={statusTone(cobranca.status)} dot>{cobranca.status}</Badge></KVRow>
        <KVRow k="end2end">
          {cobranca.end2end ? <span className="num">{cobranca.end2end}</span> : <span className="faint">— (cobrança não chegou a ser criada)</span>}
        </KVRow>
        <KVRow k="Link">
          {cobranca.link ? (
            <a href={cobranca.link} target="_blank" rel="noopener noreferrer">
              abrir link de pagamento
            </a>
          ) : (
            <Dash />
          )}
        </KVRow>
      </KV>
    </Modal>
  );
}

function NovaCobranca({ onClose, onCriada }: { onClose: () => void; onCriada: () => void }) {
  const { data } = useApp();
  const toast = useToast();
  const [titulo, setTitulo] = useState('');
  const [pagador, setPagador] = useState('');
  const [documento, setDocumento] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const emitir = async () => {
    if (!titulo.trim()) {
      setErro('Informe o título do SGP.');
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      const c = await data.createCharge({
        invoiceId: titulo.trim(),
        ...(pagador.trim() ? { payerName: pagador.trim() } : {}),
        ...(documento.trim() ? { payerDocument: documento.trim() } : {}),
      });
      toast.success(`Cobrança do título ${c.invoiceId} emitida`, `${c.status} · ${formatBRL(c.amountCents)}`);
      onCriada();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao emitir a cobrança.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal
      title="Nova cobrança"
      sub="O valor vem do SGP — o título é a autoridade, não este formulário."
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={salvando}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={() => void emitir()} loading={salvando}>
            Emitir cobrança
          </Button>
        </>
      }
    >
      {erro && <div className="notice notice--danger">{erro}</div>}
      <div className="form-grid">
        <Field label="Título (SGP)" required hint="número do título no ERP">
          <TextInput value={titulo} onChange={setTitulo} mono autoFocus placeholder="16085" onEnter={() => void emitir()} />
        </Field>
        <div className="form-row">
          <Field label="Nome do pagador" hint="opcional">
            <TextInput value={pagador} onChange={setPagador} placeholder="opcional" />
          </Field>
          <Field label="CPF/CNPJ" hint="opcional">
            <TextInput value={documento} onChange={setDocumento} mono placeholder="opcional" />
          </Field>
        </div>
      </div>
    </Modal>
  );
}
